import { defineStore } from 'pinia'
import { reactive } from 'vue'
import {
	createImageMessage as apiCreateImageMessage,
	createImageEditMessage as apiCreateImageEditMessage
} from '@/api/endpoints/images'
import { generateImage, pollImageStatus } from '@/services/generation/image'
import { startGeneration, pollGeneration } from '@/services/generation/music'
import { pollUntil } from '@/services/polling'
import { ApiError, isCopyrightError, isNetworkError } from '@/api/errors'
import { UNSUPPORTED_IMAGE_MESSAGE } from '@/helpers/imageFormats'
import AppConfig from '@/config'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import type {
	GenType,
	GenerationTask,
	PersistedTask,
	MessageObject,
	CreateMessageDataWithModel,
	CreateMusicGenerationData,
	CreateSongGenerationData
} from '@/types'
import { useThreadStore } from '@/stores/thread'
import { useComposerStore } from '@/stores/composer'
import { useCommonStore } from '@/stores/common'
import { useUsageStore } from '@/stores/usage'

const IMAGE_SAFETY_MESSAGE =
	'Запрос не может быть выполнен. Похоже на нарушение политики использования в категориях «Запрещенный контент» (18+) или «Нарушение авторских прав»'

/**
 * Task-registry генерации (Phase 5 · Step 2).
 * Задачи хранятся по threadId → генерация переживает уход с треда, возврат, F5 и идёт
 * параллельно в разных тредах. Отмена — через удаление задачи (shouldContinue в pollUntil).
 */
export const useGenerationStore = defineStore('generation', () => {
	// Реактивный реестр (ключ — threadId).
	const tasks = reactive(new Map<number, GenerationTask>())
	// threadId'ы с активным раннером в ЭТОЙ сессии (не реактивно, не персистится) —
	// защита от двойного resume (visibilitychange + onMounted).
	const running = new Set<number>()

	const isAlive = (threadId: number) => tasks.has(threadId)

	function isBusy(threadId?: number | null): boolean {
		if (threadId == null) return false
		const t = tasks.get(threadId)
		return t?.status === 'creating' || t?.status === 'pending'
	}

	function hasActiveTasks(): boolean {
		for (const t of tasks.values()) {
			if (t.status === 'creating' || t.status === 'pending') return true
		}
		return false
	}

	function upsert(task: GenerationTask) {
		tasks.set(task.threadId, task)
		persist()
	}
	function patch(threadId: number, p: Partial<GenerationTask>) {
		const t = tasks.get(threadId)
		if (t) {
			tasks.set(threadId, { ...t, ...p })
			persist()
		}
	}
	function drop(threadId: number) {
		tasks.delete(threadId)
		persist()
		// sendingMessage НЕ трогаем: лоадер/дизейбл генерации во view завязаны на isBusy(threadId),
		// а sendingMessage — глобальный флаг только для текстового стрима. Это разводит по-тредовую
		// генерацию и глобальный флаг, иначе при навигации он рассинхронизируется и залипает.
	}

	function getLastImageHash(threadId: number): string {
		const thread = useThreadStore()
		if (thread.thread.id !== threadId) return ''
		const messages = thread.thread.messages
		for (let i = messages.length - 1; i >= 0; i--) {
			const hash = messages[i].image?.hash
			if (hash) return hash
		}
		return ''
	}

	// ---- persist / hydrate ---------------------------------------------------
	function persist() {
		const arr: PersistedTask[] = [...tasks.values()]
			.filter((t) => t.status === 'creating' || t.status === 'pending')
			.map(({ threadId, type, taskId, status, prompt, meta }) => ({ threadId, type, taskId, status, prompt, meta }))
		try {
			sessionStorage.setItem(STORAGE_KEYS.GENERATION_TASKS, JSON.stringify(arr))
		} catch {
			/* sessionStorage может быть недоступен — молча игнорируем */
		}
	}
	function hydrate() {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEYS.GENERATION_TASKS)
			if (!raw) return
			const arr: PersistedTask[] = JSON.parse(raw)
			for (const p of arr) tasks.set(p.threadId, { ...p, status: 'pending' })
		} catch {
			/* повреждённый JSON — игнорируем */
		}
	}
	hydrate()

	// ---- запись результата/ошибки только если тред открыт --------------------
	function pushResultIfOpen(threadId: number, aiMessage: MessageObject) {
		const thread = useThreadStore()
		if (thread.thread.id !== threadId) return // результат уже на сервере → подтянется при getThread
		// Не дублируем трек: если музыка уже есть в треде (например, пришла через getThread) — пропускаем
		const isMusicResult = !!aiMessage.music_tracks?.length
		if (isMusicResult && thread.thread.messages.some((m) => m.music_tracks?.length)) return
		thread.thread.messages.push(aiMessage)
	}
	function setErrorIfOpen(threadId: number, message: string, showRetry: boolean) {
		const thread = useThreadStore()
		if (thread.thread.id === threadId) {
			thread.setErrorMessage(message, showRetry)
			thread.createCancelMessage()
		}
	}

	// ---- публичный API -------------------------------------------------------
	async function start(threadId: number, type: GenType, prompt: string, meta?: GenerationTask['meta']) {
		const thread = useThreadStore()
		const composer = useComposerStore()
		running.add(threadId)
		upsert({ threadId, type, taskId: null, status: 'creating', prompt, meta })
		if (thread.thread.id === threadId) thread.clearErrorMessage()
		try {
			if (type === 'image' || type === 'image_edit') {
				await runImage(threadId, type, prompt, meta)
			} else {
				await runMusicOrSong(threadId, type, prompt, meta, composer)
			}
		} catch (error: unknown) {
			handleCreateCatch(threadId, type, error)
		} finally {
			running.delete(threadId)
		}
	}

	async function runImage(threadId: number, type: GenType, prompt: string, meta?: GenerationTask['meta']) {
		const thread = useThreadStore()
		const composer = useComposerStore()
		const file = composer.currentFile.document
		const hasFile = type === 'image_edit' && !!file && !meta?.imageHash
		const imageHash = hasFile ? '' : (meta?.imageHash ?? getLastImageHash(threadId))
		if (type === 'image_edit' && !hasFile && !imageHash) {
			setErrorIfOpen(threadId, 'Прикрепите изображение для редактирования', true)
			if (thread.thread.id === threadId && thread.idMockUserMessage) {
				thread.thread.messages.pop()
				thread.idMockUserMessage = null
			}
			drop(threadId)
			return
		}
		const payload: CreateMessageDataWithModel = {
			thread_id: threadId,
			prompt,
			promt: prompt,
			model_name: thread.thread.model || AppConfig.modelNameImgGenerator,
			...(hasFile ? { image: file } : { image_hash: imageHash })
		}
		composer.clearCurrentFile()
		patch(threadId, { status: 'pending' })
		const create = () => (hasFile ? apiCreateImageEditMessage(payload) : apiCreateImageMessage(payload))
		try {
			const outcome = await generateImage(
				create,
				() => isAlive(threadId),
				(taskId) => patch(threadId, { taskId })
			)
			if (outcome.kind === 'success') {
				pushResultIfOpen(threadId, outcome.aiMessage)
				// Картинка списана сервером — забираем свежие остатки (§5.2.1, канал 2/3),
				// как после музыки и песни в poll(). Ошибку fetchUsage глушит сам.
				useUsageStore().fetchUsage()
			} else if (outcome.kind === 'failed')
				setErrorIfOpen(threadId, 'Ошибка при генерации изображения. Повторите попытку позже', true)
			drop(threadId)
		} catch (e: unknown) {
			if (isNetworkError(e)) {
				useCommonStore().openInternetErrorDialog()
				// Обрыв сети: task_id уже сохранён → НЕ дропаем, добор через resume при возврате online
				// (window 'online' → resumeAll). Если task_id ещё нет (обрыв на самом create) —
				// восстанавливать нечего, дропаем.
				if (!tasks.get(threadId)?.taskId) drop(threadId)
				return
			}
			const code = e instanceof ApiError ? (e.code ?? '') : ''
			const msg =
				code === 'UNSUPPORTED_IMAGE_FORMAT'
					? UNSUPPORTED_IMAGE_MESSAGE
					: code === 'IMAGE_SAFETY_POLICY_VIOLATION'
						? IMAGE_SAFETY_MESSAGE
						: 'Ошибка при генерации изображения. Повторите попытку позже'
			setErrorIfOpen(threadId, msg, false)
			drop(threadId)
		}
	}

	async function runMusicOrSong(
		threadId: number,
		type: GenType,
		prompt: string,
		meta: GenerationTask['meta'] | undefined,
		composer: ReturnType<typeof useComposerStore>
	) {
		const thread = useThreadStore()
		const model_name = composer.getModelName(thread.thread.model)
		if (type === 'song' && thread.thread.id === threadId) {
			thread.thread.meta = { ...thread.thread.meta, song_style: meta?.style }
		}
		composer.clearCurrentFile()
		const payload =
			type === 'song'
				? ({
						thread_id: threadId,
						model_name,
						prompt,
						style: meta?.style ?? '',
						vocal_gender: meta?.vocal_gender ?? 'm'
					} as CreateSongGenerationData)
				: ({ thread_id: threadId, model_name, prompt } as CreateMusicGenerationData)

		const started = await startGeneration(type === 'song' ? 'song' : 'music', payload)
		if (!started.ok) {
			setErrorIfOpen(threadId, started.message, false)
			drop(threadId)
			return
		}
		patch(threadId, { status: 'pending', taskId: started.taskId })
		await poll(threadId, started.taskId)
	}

	async function poll(threadId: number, taskId: string, opts?: { onError?: () => Promise<void> }) {
		try {
			const outcome = await pollGeneration(taskId, () => isAlive(threadId))
			if (outcome.kind === 'success') {
				pushResultIfOpen(threadId, outcome.aiMessage)
				// Музыка/песня списана сервером — забираем свежие остатки (§5.2.1, канал 2/3).
				// Срез из тела ответа генерации пока не разбираем: pollGeneration отдаёт только
				// исход, поэтому обходимся запросом. Ошибку fetchUsage глушит сам.
				useUsageStore().fetchUsage()
			} else if (outcome.kind === 'copyright') setErrorIfOpen(threadId, outcome.message, false)
			else if (outcome.kind === 'failed') setErrorIfOpen(threadId, 'Кажется что-то пошло не так', true)
			drop(threadId)
		} catch (error: any) {
			// Текст про копирайт бэк присылает готовым для пользователя — показываем как есть.
			if (isCopyrightError(error?.code) && error?.raw) {
				setErrorIfOpen(threadId, error.raw, false)
				drop(threadId)
				return
			}
			if (isNetworkError(error)) {
				// Обрыв сети: показываем диалог, НЕ дропаем — добор при возврате online
				// (resumeAll → resume → poll) / F5. Проверяем раньше onError, чтобы не уходить
				// в getThread-фолбэк вместо ожидания сети.
				useCommonStore().openInternetErrorDialog()
				return
			}
			// Режим резюма: статус-опрос упал (напр. бэкенд не признаёт старый task_id) —
			// не теряем задачу, откатываемся на getThread-фолбэк (задачу дропнет он).
			if (opts?.onError) {
				await opts.onError()
				return
			}
			console.error('Polling error:', error)
			setErrorIfOpen(threadId, 'Кажется что-то пошло не так', true)
			drop(threadId)
		}
	}

	function handleCreateCatch(threadId: number, type: GenType, error: any) {
		if (isNetworkError(error)) {
			// Обрыв сети на старте генерации: показываем диалог, НЕ дропаем — при возврате online
			// resumeAll → resume → getThread-фолбэк (resumeViaThread) добирает результат, если
			// генерация всё же стартовала на сервере.
			useCommonStore().openInternetErrorDialog()
			return
		}
		if (isCopyrightError(error?.code) && error?.raw) {
			setErrorIfOpen(threadId, error.raw, false)
		} else {
			console.error('Generation error', error)
			const generic =
				type === 'song' ? 'Ошибка генерации песни' : type === 'music' ? 'Ошибка генерации музыки' : 'Ошибка генерации'
			setErrorIfOpen(threadId, error instanceof Error ? 'Кажется что-то пошло не так' : generic, true)
		}
		drop(threadId)
	}

	// Отмена: удалить задачу → pollUntil выйдет на следующей итерации по shouldContinue.
	function cancel(threadId: number) {
		drop(threadId)
	}

	const hasMusicResult = (threadId: number) => {
		const thread = useThreadStore()
		return thread.thread.id === threadId && thread.thread.messages.some((m) => m.music_tracks?.length)
	}

	// Фолбэк для music/song без taskId (F5 в момент create-POST): task_id потерян, но генерация
	// идёт на сервере. Тянем результат ТИХИМ опросом треда (fetchThreadSilent: без глобального
	// loading и без мутации стора), пока не появится трек — сервер тут истина.
	// Стор трогаем один раз, финальным applyThread: иначе интерфейс мигал бы каждые 4с, а
	// ensureUserPrompt после каждого перезатирания дописывал промпт в хвост (дубль после ответа ИИ).
	async function resumeViaThread(threadId: number) {
		const thread = useThreadStore()
		if (hasMusicResult(threadId)) {
			// Результат уже в открытом треде (приехал с getThread) — ждать нечего
			drop(threadId)
			return
		}
		let attempts = 0
		const MAX_ATTEMPTS = 150 // ~10 мин при delay 4с
		try {
			const data = await pollUntil(() => thread.fetchThreadSilent(threadId), {
				delay: 4000,
				shouldContinue: () => isAlive(threadId) && attempts++ < MAX_ATTEMPTS,
				isPending: (t) => !t?.messages?.some((m) => m.music_tracks?.length),
				shouldRetry: () => true // сетевые сбои опроса не должны прерывать резюм
			})
			if (data) {
				if (thread.thread.id === threadId) thread.applyThread(data)
			} else if (isAlive(threadId)) {
				// null при живой задаче = исчерпан лимит попыток (отмена дропает задачу сама).
				// Без этого лоадер молча исчезал бы без результата и без объяснения.
				setErrorIfOpen(threadId, 'Кажется что-то пошло не так', true)
			}
		} catch (e) {
			console.error('resume via getThread error', e)
		} finally {
			drop(threadId)
		}
	}

	// Возобновление после навигации / visibilitychange / F5.
	// Гейта здесь нет намеренно: единица ресурса за эту задачу уже списана, и запереть
	// дотягивание результата (исчерпанный лимит, сорванное продление) — значит потерять
	// то, за что человек заплатил. Гейт стоит только на создании новых задач, в views.
	async function resume(threadId: number) {
		const t = tasks.get(threadId)
		if (!t) return
		if (running.has(threadId)) return // уже идёт в этой сессии
		if (t.type === 'music' || t.type === 'song') {
			// Трек уже в открытом треде — задача завершена, сеть дёргать не нужно
			if (hasMusicResult(threadId)) {
				drop(threadId)
				return
			}
			running.add(threadId)
			patch(threadId, { status: 'pending' })
			try {
				// Есть taskId — лёгкий опрос статуса, а при его ошибке — фолбэк на getThread.
				// Нет taskId (F5 в момент create) — сразу getThread-фолбэк.
				// Дубли трека гасит pushResultIfOpen / getThread-дедуп.
				if (t.taskId) await poll(threadId, t.taskId, { onError: () => resumeViaThread(threadId) })
				else await resumeViaThread(threadId)
			} finally {
				running.delete(threadId)
			}
			return
		}
		// image → добор по task_id (повторный опрос статуса), иначе фолбэк на getThread.
		running.add(threadId)
		patch(threadId, { status: 'pending' })
		try {
			if (t.taskId) {
				const outcome = await pollImageStatus(t.taskId, () => isAlive(threadId))
				if (outcome.kind === 'success') {
					pushResultIfOpen(threadId, outcome.aiMessage)
					// Картинка списана сервером — забираем свежие остатки (§5.2.1, канал 2/3),
					// как после музыки и песни в poll(). Ошибку fetchUsage глушит сам.
					useUsageStore().fetchUsage()
				} else if (outcome.kind === 'failed')
					setErrorIfOpen(threadId, 'Ошибка при генерации изображения. Повторите попытку позже', true)
			} else {
				await useThreadStore().getThread(threadId)
				// getThread перезатирает messages — возвращаем видимый промпт под лоадером
				if (t.prompt) useThreadStore().ensureUserPrompt(t.prompt)
			}
		} catch (e: unknown) {
			// Снова нет сети — оставляем таск на следующий resume (online / visibilitychange / F5).
			if (isNetworkError(e)) {
				useCommonStore().openInternetErrorDialog()
				return // finally снимет running; таск остаётся pending до следующего resume
			}
			console.error('resume image error', e)
		} finally {
			running.delete(threadId)
		}
		drop(threadId)
	}

	// Возврат сети (window 'online' в App.vue) — добрать все зависшие задачи.
	function resumeAll() {
		for (const threadId of [...tasks.keys()]) {
			void resume(threadId)
		}
	}

	return {
		tasks,
		isBusy,
		hasActiveTasks,
		getLastImageHash,
		start,
		cancel,
		resume,
		resumeAll
	}
})
